import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Check, X, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FIELD_COLUMNS = [
  { key: "birthdate", label: "Birthdate", type: "date" },
  { key: "startDate", label: "Start Date", type: "date" },
  { key: "category", label: "Category", type: "select", options: [
    { value: "full_time", label: "Full Time" },
    { value: "part_time", label: "Part Time" },
    { value: "school_age", label: "School Age" },
    { value: "after_school", label: "After School Program" },
  ]},
  { key: "school", label: "School", type: "text" },
  { key: "guardian1_name", label: "Parent/Guardian 1", type: "text" },
  { key: "guardian1_phone", label: "Contact Phone 1", type: "text" },
  { key: "guardian1_email", label: "Email 1", type: "text" },
  { key: "guardian2_name", label: "Parent/Guardian 2", type: "text" },
  { key: "guardian2_phone", label: "Contact Phone 2", type: "text" },
  { key: "guardian2_email", label: "Email 2", type: "text" },
  { key: "guardian3_name", label: "Parent/Guardian 3", type: "text" },
  { key: "guardian3_phone", label: "Contact Phone 3", type: "text" },
  { key: "guardian3_email", label: "Email 3", type: "text" },
  { key: "guardian4_name", label: "Parent/Guardian 4", type: "text" },
  { key: "guardian4_phone", label: "Contact Phone 4", type: "text" },
  { key: "guardian4_email", label: "Email 4", type: "text" },
  { key: "guardian5_name", label: "Parent/Guardian 5", type: "text" },
  { key: "guardian5_phone", label: "Contact Phone 5", type: "text" },
  { key: "guardian5_email", label: "Email 5", type: "text" },
  { key: "specialInstructions", label: "Special Instructions", type: "text" },
];

function extractField(child, key) {
  if (key === "birthdate") return child.birthdate || "";
  if (key === "startDate") return child.startDate || "";
  if (key === "category") return child.category || "";
  if (key === "school") return child.school || "";
  if (key === "specialInstructions") return child.specialInstructions || "";
  const match = key.match(/^guardian(\d)_(name|phone|email)$/);
  if (match) {
    const idx = parseInt(match[1]) - 1;
    const field = match[2];
    const person = (child.pickupPersons || [])[idx];
    if (!person) return "";
    return person[field] || "";
  }
  return "";
}

function displayValue(key, val) {
  if ((key === "birthdate" || key === "startDate") && val) {
    const parts = val.split("-");
    if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
    return val;
  }
  if (key === "category") {
    const map = { full_time: "Full Time", part_time: "Part Time", school_age: "School Age", after_school: "After School Program" };
    return map[val] || val;
  }
  return val;
}

// Parse mismatch details
function parseMismatchDetails(details) {
  return details.map((d) => {
    const m = d.match(/^(.+?):\s*Template='(.*)'\s*vs\s*App='(.*)'/);
    if (m) return { field: m[1].trim(), templateVal: m[2], appVal: m[3] };
    const n = d.match(/Pickup Names in Template not in App: (.+)/);
    if (n) return { field: "New Guardians in Template", templateVal: n[1], appVal: "(not in app)" };
    return { field: d, templateVal: "", appVal: "" };
  });
}

function MismatchBanner({ child, onResolve, resolving }) {
  const info = child.mismatchInfo;
  if (!info || !info.hasMismatch) return null;
  const parsed = parseMismatchDetails(info.mismatchDetails || []);
  return (
    <tr>
      <td colSpan={FIELD_COLUMNS.length + 1} className="p-0">
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 mx-1 my-1 rounded-r-lg">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 text-sm">Template vs App Mismatch for {child.name}</p>
              <p className="text-xs text-amber-600 mt-0.5">Review below and choose which to keep.</p>
            </div>
          </div>
          <table className="text-xs border-collapse mt-2" style={{ maxWidth: 700 }}>
            <thead><tr>
              <th className="text-left px-2 py-1 bg-amber-100 border border-amber-200 font-semibold text-amber-800" style={{ width: 180 }}>Field</th>
              <th className="text-left px-2 py-1 bg-red-50 border border-amber-200 font-semibold text-red-700" style={{ width: 250 }}>Template Says</th>
              <th className="text-left px-2 py-1 bg-green-50 border border-amber-200 font-semibold text-green-700" style={{ width: 250 }}>App Has (Current)</th>
            </tr></thead>
            <tbody>{parsed.map((p, i) => (
              <tr key={i}>
                <td className="px-2 py-1.5 border border-amber-200 font-medium text-gray-700">{p.field}</td>
                <td className="px-2 py-1.5 border border-amber-200 bg-red-50 text-red-800">{p.templateVal || <span className="text-gray-400">empty</span>}</td>
                <td className="px-2 py-1.5 border border-amber-200 bg-green-50 text-green-800">{p.appVal || <span className="text-gray-400">empty</span>}</td>
              </tr>
            ))}</tbody>
          </table>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="border-green-500 text-green-700 hover:bg-green-50 text-xs h-8"
              onClick={() => onResolve(child.id, "keep_app")} disabled={resolving} data-testid={`keep-app-${child.id}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Keep App Data
            </Button>
            <Button size="sm" variant="outline" className="border-red-500 text-red-700 hover:bg-red-50 text-xs h-8"
              onClick={() => onResolve(child.id, "accept_template")} disabled={resolving} data-testid={`accept-template-${child.id}`}>
              <X className="w-3.5 h-3.5 mr-1" /> Use Template Data
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
}

const SignInBulkGrid = ({ children: childrenProp, onRefresh, loading }) => {
  const [resolving, setResolving] = useState(false);
  const [gridData, setGridData] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize grid data from children prop
  useEffect(() => {
    const data = {};
    childrenProp.forEach(child => {
      data[child.id] = {};
      FIELD_COLUMNS.forEach(col => {
        data[child.id][col.key] = extractField(child, col.key);
      });
    });
    setGridData(data);
    setPendingChanges({});
  }, [childrenProp]);

  const handleCellChange = (childId, key, value) => {
    setGridData(prev => ({
      ...prev,
      [childId]: { ...prev[childId], [key]: value }
    }));
    setPendingChanges(prev => ({
      ...prev,
      [`${childId}:${key}`]: { childId, key, value }
    }));
  };

  const handleSaveAll = async () => {
    const changes = Object.values(pendingChanges);
    if (changes.length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      // Group changes by child
      const byChild = {};
      changes.forEach(c => {
        if (!byChild[c.childId]) byChild[c.childId] = {};
        byChild[c.childId][c.key] = c.value;
      });

      let saved = 0;
      for (const [childId, fields] of Object.entries(byChild)) {
        const child = childrenProp.find(c => c.id === childId);
        if (!child) continue;

        const updatePayload = {};
        const pickupPersons = [...(child.pickupPersons || [])];

        for (const [key, value] of Object.entries(fields)) {
          if (key === "birthdate") updatePayload.birthdate = value;
          else if (key === "startDate") updatePayload.startDate = value;
          else if (key === "category") updatePayload.category = value;
          else if (key === "school") updatePayload.school = value;
          else if (key === "specialInstructions") updatePayload.specialInstructions = value;
          else {
            const match = key.match(/^guardian(\d)_(name|phone|email)$/);
            if (match) {
              const idx = parseInt(match[1]) - 1;
              while (pickupPersons.length <= idx) {
                pickupPersons.push({ name: "", phone: "", email: "" });
              }
              pickupPersons[idx][match[2]] = value;
            }
          }
        }

        // Check if any pickup fields changed
        const hasPickupChanges = Object.keys(fields).some(k => k.match(/^guardian\d/));
        if (hasPickupChanges) updatePayload.pickupPersons = pickupPersons;

        await axios.put(`${API}/children/${childId}`, updatePayload);
        saved++;
      }

      setPendingChanges({});
      toast.success(`Saved changes for ${saved} children`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (childId, action) => {
    setResolving(true);
    try {
      const child = childrenProp.find(c => c.id === childId);
      const info = child?.mismatchInfo;
      let templateValues = {};
      if (action === "accept_template" && info?.mismatchDetails) {
        const parsed = parseMismatchDetails(info.mismatchDetails);
        const pickupUpdates = [];
        for (const p of parsed) {
          if (p.field === "Category") templateValues.category = p.templateVal;
          else if (p.field === "School") templateValues.school = p.templateVal;
          else if (p.field === "Birthdate") templateValues.birthdate = p.templateVal;
          else if (p.field.startsWith("Phone for")) {
            const nm = p.field.match(/Phone for '(.+)'/);
            if (nm) pickupUpdates.push({ name: nm[1], phone: p.templateVal });
          } else if (p.field.startsWith("Email for")) {
            const nm = p.field.match(/Email for '(.+)'/);
            if (nm) {
              const ex = pickupUpdates.find(u => u.name === nm[1]);
              if (ex) ex.email = p.templateVal;
              else pickupUpdates.push({ name: nm[1], email: p.templateVal });
            }
          }
        }
        if (pickupUpdates.length) templateValues.pickupUpdates = pickupUpdates;
      }
      await axios.post(`${API}/children/${childId}/resolve-mismatch`, { action, templateValues });
      toast.success(action === "keep_app" ? "Kept app data" : "Applied template data");
      onRefresh();
    } catch { toast.error("Failed to resolve mismatch"); }
    finally { setResolving(false); }
  };

  const handleRefresh = () => {
    if (hasPending && !window.confirm("You have unsaved changes. Refresh will discard them. Continue?")) return;
    onRefresh();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-600 mr-2" />
        <span className="text-teal-700">Loading children...</span>
      </div>
    );
  }

  if (!childrenProp || childrenProp.length === 0) {
    return <div className="text-center py-8 text-gray-500">No active children found. Add children via Enrolled Children first.</div>;
  }

  const hasPending = Object.keys(pendingChanges).length > 0;
  const mismatchCount = childrenProp.filter(c => c.mismatchInfo?.hasMismatch).length;

  return (
    <div data-testid="signin-bulk-grid">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">
            Edit enrollment info directly — changes here sync to <strong>Enrolled Children</strong>. You can also bulk upload this information using the <strong>Upload Template</strong> tab above.
            {hasPending && <span className="ml-2 text-orange-600 font-medium">({Object.keys(pendingChanges).length} unsaved)</span>}
          </p>
          {mismatchCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full border border-amber-300" data-testid="mismatch-count-badge">
              <AlertTriangle className="w-3.5 h-3.5" />
              {mismatchCount} mismatch{mismatchCount > 1 ? "es" : ""}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="signin-grid-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={handleSaveAll} disabled={!hasPending || saving} size="sm"
            className={hasPending ? "bg-green-600 hover:bg-green-700" : "bg-gray-400"}
            data-testid="signin-grid-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-auto" style={{ maxHeight: 600 }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-teal-700 text-white text-xs font-bold px-3 py-2 border-r border-b border-teal-600 text-left whitespace-nowrap" style={{ minWidth: 180 }}>
                Child's Name
              </th>
              {FIELD_COLUMNS.map(col => (
                <th key={col.key} className="bg-teal-600 text-white text-xs font-bold px-3 py-2 border-r border-b border-teal-500 text-center whitespace-nowrap" style={{ minWidth: 140 }}>
                  {col.label}
                  {col.label.startsWith("Parent/Guardian") && (
                    <div className="text-[10px] font-normal text-teal-100 normal-case">Pickup Person</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {childrenProp.map((child, rowIdx) => {
              const hasMismatch = child.mismatchInfo?.hasMismatch;
              return (
                <>
                  <tr key={child.id} className={hasMismatch ? "bg-amber-50" : rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className={`sticky left-0 z-[5] text-xs font-semibold px-3 py-1.5 border-r border-b whitespace-nowrap ${hasMismatch ? "bg-amber-100 text-amber-900" : "bg-teal-50 text-teal-800"}`} style={{ minWidth: 180 }}>
                      {hasMismatch && <AlertTriangle className="w-3 h-3 text-amber-600 inline mr-1" />}
                      {child.name}
                    </td>
                    {FIELD_COLUMNS.map(col => {
                      const val = gridData[child.id]?.[col.key] ?? "";
                      const isPending = pendingChanges[`${child.id}:${col.key}`];
                      if (col.type === "select") {
                        return (
                          <td key={col.key} className={`px-1 py-1 border-r border-b ${isPending ? "bg-yellow-50" : ""}`}>
                            <select value={val} onChange={e => handleCellChange(child.id, col.key, e.target.value)}
                              className="w-full border rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-teal-400"
                              data-testid={`grid-${child.id}-${col.key}`}>
                              <option value="">--</option>
                              {col.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                        );
                      }
                      if (col.type === "date") {
                        return (
                          <td key={col.key} className={`px-1 py-1 border-r border-b ${isPending ? "bg-yellow-50" : ""}`}>
                            <input type="date" value={val} onChange={e => handleCellChange(child.id, col.key, e.target.value)}
                              className="w-full border rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-teal-400"
                              data-testid={`grid-${child.id}-${col.key}`} />
                          </td>
                        );
                      }
                      return (
                        <td key={col.key} className={`px-1 py-1 border-r border-b ${isPending ? "bg-yellow-50" : ""}`}>
                          <input type="text" value={val} onChange={e => handleCellChange(child.id, col.key, e.target.value)}
                            className="w-full border rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-teal-400"
                            placeholder="-"
                            data-testid={`grid-${child.id}-${col.key}`} />
                        </td>
                      );
                    })}
                  </tr>
                  {hasMismatch && (
                    <MismatchBanner key={`mismatch-${child.id}`} child={child} onResolve={handleResolve} resolving={resolving} />
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignInBulkGrid;
